import type { LucideIcon } from 'lucide-react';
import {
  MessageSquare,
  Search,
  FolderOpen,
  Briefcase,
  CalendarDays,
  Mic,
  FileText,
  FileSignature,
  FileStack,
  Package,
  MessageCircle,
  Brain,
  Users,
  Settings,
  ChevronDown,
} from 'lucide-react';
import { PRIMARY_PORTAL_NAV_IDS } from './portalNavIds';
import { kanzleiChatNavVisible } from './elementSeatInvite';
import { isHonestMatrixRoomId } from './portalMatrixOrigins';
import { portalViewAllowed, seatEntersBaustein } from './portalBausteinSitz';
import type { BausteinSeatInput } from './portalBausteinUnlock';

/** Sidebar/Mehr: Produktname ohne Engine-Jargon. */
export const PORTAL_NAV_LITIGATION_LABEL = 'Fallanalyse';
export const PORTAL_NAV_MATRIX_LABEL = 'Kanzlei-Chat';
export const PORTAL_NAV_VERTRAG_QA_LABEL = 'Vertragsprüfung';
/**
 * Demo-Nav (Mehr + Palette): Fläche nicht öffnen.
 * View, Workflow und Label bleiben fürs spätere Update.
 */
export const PORTAL_DEMO_NAV_HIDDEN_VIEWS: readonly PortalView[] = ['vertrags_qa'];

export function isDemoNavHiddenView(view: string): boolean {
  return (PORTAL_DEMO_NAV_HIDDEN_VIEWS as readonly string[]).includes(view);
}
/** Mehr-Reiter. Namen der Einträge: RECHTSTEXTE Abschnitt E. */
export const PORTAL_NAV_BAUSTEIN_LABEL = 'Baustein';
/** Unter Mehr — gleiche View `gedaechtnis` (Skills, Interview). Sicht wie bisher: Premium und Curator-URL. */
export const PORTAL_NAV_BRAIN_LABEL = 'Brain';

export type PortalView =
  | 'home'
  | 'chat'
  | 'suche'
  | 'dokumente'
  | 'akten'
  | 'kalender'
  | 'diktat'
  | 'upload'
  | 'protokolle'
  | 'bausteine'
  | 'litigation'
  | 'vertrags_qa'
  | 'contract_generator'
  | 'document_editor'
  | 'pdf_tools'
  | 'gedaechtnis'
  | 'tasten'
  | 'feedback'
  | 'matrix'
  | 'settings'
  | 'kanzlei_zugaenge';

/** Diktat-Reiter: Aufnehmen (Mikrofon) | Hochladen | Handy/Element. */
export type DiktatTab = 'record' | 'upload' | 'anleitung';

/** Unter-Reiter im Dokumente-Bereich bei geöffnetem Editor (OnlyOffice + ARKO). */
export type DokumenteAreaTab = 'editor' | 'hub';

export interface NavItemConfig {
  id: PortalView;
  icon: LucideIcon;
  label: string;
  badge?: 'beta' | 'neu';
}

export interface PortalNavContext {
  isPremium: boolean;
  contractGeneratorEnabled: boolean;
  /** MEMCORE Modul B (Labor): Brain-Reiter + Speicher-Flows sichtbar. */
  memcoreEnabled?: boolean;
  /** Professional/Premium IT-Admin / primärer Account — Sidebar „Kanzlei & Zugänge“. */
  teamAccessEnabled?: boolean;
  /** Beta-Tester: Feedback unten in der Sidebar, nicht in der Tool-Leiste. */
  betaTester?: boolean;
  /** Eigenes Element am Sitz — nur dann Reiter Kanzlei-Chat. */
  elementSeat?: boolean;
  /** Sitz-Bausteine. Ohne Sitz bleiben ARKO, Suche, Diktat und Fallanalyse zu. */
  seat?: BausteinSeatInput;
}

/** PB-Feld matrixRoomId — echte Room-Id, keine Stubs. */
export function hasMatrixRoom(matrixRoomId: string | undefined | null): boolean {
  return isHonestMatrixRoomId(matrixRoomId);
}

const PRIMARY_NAV_META: Record<(typeof PRIMARY_PORTAL_NAV_IDS)[number], { icon: LucideIcon; label: string }> = {
  chat: { icon: MessageSquare, label: 'ARKO' },
  suche: { icon: Search, label: 'Suche' },
  diktat: { icon: Mic, label: 'Diktat' },
  dokumente: { icon: FolderOpen, label: 'Dokumente' },
  akten: { icon: Briefcase, label: 'Akten' },
  kalender: { icon: CalendarDays, label: 'Kalender' },
};

/** Primäre Sidebar — ARKO · Suche · Diktat · Dokumente · Akten · Kalender · Kanzlei-Chat. Brain liegt unter Mehr. */
export function getPrimaryNavItems(
  ctx?: Pick<PortalNavContext, 'elementSeat'>,
): NavItemConfig[] {
  const items: NavItemConfig[] = PRIMARY_PORTAL_NAV_IDS.map((id) => ({
    id,
    icon: PRIMARY_NAV_META[id].icon,
    label: PRIMARY_NAV_META[id].label,
  }));
  if (kanzleiChatNavVisible(ctx?.elementSeat)) {
    items.push({ id: 'matrix', icon: Users, label: PORTAL_NAV_MATRIX_LABEL });
  }
  return items;
}

/** Konto-Footer — Einstellungen. Kanzlei-Chat hängt an der Primärnav, nicht hier. */
export function getAccountNavItems(): NavItemConfig[] {
  return [{ id: 'settings', icon: Settings, label: 'Einstellungen' }];
}

/** Einklappbare Zusatznavigation. Brain nur für dieselbe Sicht wie bisher (Premium und Curator-URL). */
export function getMehrNavItems(ctx: PortalNavContext): NavItemConfig[] {
  const items: NavItemConfig[] = [];
  if (ctx.isPremium && ctx.memcoreEnabled) {
    items.push({ id: 'gedaechtnis', icon: Brain, label: PORTAL_NAV_BRAIN_LABEL });
  }
  if (ctx.teamAccessEnabled) {
    items.push({ id: 'kanzlei_zugaenge', icon: Users, label: 'Kanzlei & Zugänge' });
  }
  if (seatEntersBaustein('fallanalyse', ctx.seat ?? {})) {
    items.push({ id: 'litigation', icon: FileText, label: PORTAL_NAV_LITIGATION_LABEL });
  }
  if (ctx.isPremium && ctx.contractGeneratorEnabled) {
    items.push({ id: 'contract_generator', icon: FileSignature, label: 'Vertrag erstellen' });
  }
  items.push({ id: 'pdf_tools', icon: FileStack, label: 'PDF-Werkzeuge' });
  items.push({ id: 'bausteine', icon: Package, label: PORTAL_NAV_BAUSTEIN_LABEL });
  if (!ctx.betaTester) {
    items.push({ id: 'feedback', icon: MessageCircle, label: 'Feedback' });
  }
  return items;
}

/** Minimaler Titel — keine Marketing-Subtitles in der Hauptfläche. */
export function portalViewTitle(view: PortalView): string {
  switch (view) {
    case 'home':
      return 'Start';
    case 'chat':
      return 'ARKO';
    case 'suche':
      return 'Suche';
    case 'dokumente':
    case 'protokolle':
      return 'Dokumente';
    case 'akten':
      return 'Akten';
    case 'kalender':
      return 'Kalender';
    case 'diktat':
    case 'upload':
      return 'Diktat';
    case 'bausteine':
      return PORTAL_NAV_BAUSTEIN_LABEL;
    case 'litigation':
      return PORTAL_NAV_LITIGATION_LABEL;
    case 'vertrags_qa':
      return PORTAL_NAV_VERTRAG_QA_LABEL;
    case 'contract_generator':
      return 'Vertrag erstellen';
    case 'document_editor':
      return 'Dokument';
    case 'pdf_tools':
      return 'PDF-Werkzeuge';
    case 'gedaechtnis':
      return PORTAL_NAV_BRAIN_LABEL;
    case 'tasten':
      return 'Tasten';
    case 'feedback':
      return 'Feedback';
    case 'matrix':
      return PORTAL_NAV_MATRIX_LABEL;
    case 'settings':
      return 'Einstellungen';
    case 'kanzlei_zugaenge':
      return 'Kanzlei & Zugänge';
    default:
      return 'ARKORBIT Portal';
  }
}

/** Welche View in der Sidebar als aktiv gilt. */
export function resolveSidebarActiveView(view: PortalView): PortalView {
  if (view === 'protokolle' || view === 'document_editor') return 'dokumente';
  if (view === 'upload') return 'diktat';
  return view;
}

/** Views in „Mehr“, die das Untermenü offen halten. */
export const MEHR_VIEWS: PortalView[] = [
  'gedaechtnis',
  'kanzlei_zugaenge',
  'litigation',
  'vertrags_qa',
  'contract_generator',
  'pdf_tools',
  'bausteine',
  'feedback',
];

export function isMehrViewActive(view: PortalView): boolean {
  return MEHR_VIEWS.includes(view);
}

/** Chevron für Mehr-Gruppe (re-export für Sidebar). */
export { ChevronDown };

/** Persistiert in sessionStorage (Tab-Reload, kein localStorage — Logout löscht). */
export interface PortalSessionDocument {
  display_name: string;
  document_url: string;
  pb_record_id?: string;
  nextcloud_path?: string;
}

export interface PortalSessionState {
  version: 1;
  view: PortalView;
  document?: PortalSessionDocument | null;
  /** Letzter ARKO-Chip/Intent (Label, kein PII). */
  lastArkoIntent?: string | null;
}

export type PortalHomeRecentKind = 'document' | 'arko' | 'view';

export interface PortalHomeRecentItem {
  id: string;
  kind: PortalHomeRecentKind;
  label: string;
  sublabel: string;
  view?: PortalView;
  document?: PortalSessionDocument;
}

const PORTAL_SESSION_VERSION = 1 as const;

const RESTORABLE_VIEWS: PortalView[] = [
  'home',
  'chat',
  'suche',
  'dokumente',
  'akten',
  'kalender',
  'diktat',
  'bausteine',
  'feedback',
  'litigation',
  'vertrags_qa',
  'contract_generator',
  'document_editor',
  'pdf_tools',
  'gedaechtnis',
  'tasten',
  'matrix',
  'kanzlei_zugaenge',
];

export function portalSessionStorageKey(customerId: string): string {
  return `arkorbit-portal-session:v${PORTAL_SESSION_VERSION}:${customerId}`;
}

function isPortalView(value: unknown): value is PortalView {
  return typeof value === 'string' && RESTORABLE_VIEWS.includes(value as PortalView);
}

function isPortalSessionDocument(value: unknown): value is PortalSessionDocument {
  if (!value || typeof value !== 'object') return false;
  const d = value as PortalSessionDocument;
  return (
    typeof d.display_name === 'string' &&
    d.display_name.trim().length > 0 &&
    typeof d.document_url === 'string' &&
    /^https?:\/\//i.test(d.document_url.trim())
  );
}

export function loadPortalSessionState(customerId: string): PortalSessionState | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(portalSessionStorageKey(customerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const v = parsed as Partial<PortalSessionState>;
    const rawView = v.view;
    if (v.version !== PORTAL_SESSION_VERSION || rawView === undefined) return null;
    const restoredView: PortalView | null = isPortalView(rawView) ? rawView : null;
    if (!restoredView) return null;
    const view = isDemoNavHiddenView(restoredView) ? 'home' : restoredView;
    const doc = v.document === null || v.document === undefined
      ? undefined
      : isPortalSessionDocument(v.document)
        ? v.document
        : undefined;
    const lastArkoIntent =
      typeof v.lastArkoIntent === 'string' && v.lastArkoIntent.trim()
        ? v.lastArkoIntent.trim()
        : v.lastArkoIntent === null
          ? null
          : undefined;
    return { version: PORTAL_SESSION_VERSION, view, document: doc, lastArkoIntent };
  } catch {
    return null;
  }
}

export function savePortalSessionState(
  customerId: string,
  partial: Partial<Omit<PortalSessionState, 'version'>>,
): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const prev = loadPortalSessionState(customerId);
    const next: PortalSessionState = {
      version: PORTAL_SESSION_VERSION,
      view: partial.view ?? prev?.view ?? 'home',
      document: partial.document !== undefined ? partial.document : prev?.document,
      lastArkoIntent:
        partial.lastArkoIntent !== undefined ? partial.lastArkoIntent : prev?.lastArkoIntent,
    };
    sessionStorage.setItem(portalSessionStorageKey(customerId), JSON.stringify(next));
  } catch {
    /* sessionStorage voll oder blockiert */
  }
}

export function clearPortalSessionState(customerId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(portalSessionStorageKey(customerId));
  } catch {
    /* ignore */
  }
}

/** Kandidaten für Schnellzugriff und Zuletzt — die Ansicht filtert die doppelte Datei. */
export function buildPortalHomeRecents(
  customerId: string,
  recentDocs: Array<{
    display_name: string;
    document_url: string;
    pb_record_id?: string;
    nextcloud_path?: string;
  }>,
  options?: { lastArkoIntent?: string | null },
): PortalHomeRecentItem[] {
  const session = loadPortalSessionState(customerId);
  const lastArkoIntent = options?.lastArkoIntent ?? session?.lastArkoIntent;
  const items: PortalHomeRecentItem[] = [];
  const seen = new Set<string>();

  const push = (item: PortalHomeRecentItem) => {
    if (seen.has(item.id) || items.length >= 8) return;
    seen.add(item.id);
    items.push(item);
  };

  const sessionDoc = session?.document;
  const recentDoc = recentDocs[0];
  if (sessionDoc) {
    push({
      id: `doc-${sessionDoc.document_url}`,
      kind: 'document',
      label: sessionDoc.display_name,
      sublabel: 'Zuletzt geöffnet',
      view: 'document_editor',
      document: sessionDoc,
    });
  } else if (recentDoc?.document_url && recentDoc.display_name) {
    push({
      id: `doc-${recentDoc.document_url}`,
      kind: 'document',
      label: recentDoc.display_name,
      sublabel: 'Zuletzt geöffnet',
      view: 'document_editor',
      document: {
        display_name: recentDoc.display_name,
        document_url: recentDoc.document_url,
        pb_record_id: recentDoc.pb_record_id,
        nextcloud_path: recentDoc.nextcloud_path,
      },
    });
  }

  for (const doc of recentDocs) {
    const url = doc.document_url?.trim();
    const name = doc.display_name?.trim();
    if (!url || !name) continue;
    push({
      id: `doc-${url}`,
      kind: 'document',
      label: name,
      sublabel: 'Zuletzt geöffnet',
      view: 'document_editor',
      document: {
        display_name: name,
        document_url: url,
        pb_record_id: doc.pb_record_id,
        nextcloud_path: doc.nextcloud_path,
      },
    });
  }

  const hadChat =
    session?.view === 'chat' ||
    session?.view === 'document_editor' ||
    Boolean(lastArkoIntent?.trim());
  if (hadChat) {
    push({
      id: 'arko-doc',
      kind: 'arko',
      label: session?.view === 'document_editor' ? 'ARKO am Dokument' : 'ARKO',
      sublabel: lastArkoIntent?.trim() || (session?.view === 'document_editor' ? 'Dokument öffnen für ARKO' : 'Kanzlei-Assistent'),
      view: session?.view === 'document_editor' ? 'dokumente' : 'chat',
    });
  }

  const lastView = session?.view;
  const hasDoc = Boolean(sessionDoc || recentDoc);
  if (
    lastView &&
    lastView !== 'home' &&
    lastView !== 'chat' &&
    lastView !== 'document_editor' &&
    !hasDoc
  ) {
    push({
      id: `view-${lastView}`,
      kind: 'view',
      label: portalViewTitle(lastView),
      sublabel: 'Zuletzt besucht',
      view: lastView,
    });
  }

  return items;
}

/** Bis zu drei andere Dinge. Die Datei der Dokument-Karte im Schnellzugriff fehlt hier. */
export function portalHomeZuletztItems(
  items: readonly PortalHomeRecentItem[],
  schnellzugriffDocId: string,
): PortalHomeRecentItem[] {
  const skip = schnellzugriffDocId.trim();
  const out: PortalHomeRecentItem[] = [];
  for (const item of items) {
    if (skip && item.id === skip) continue;
    if (out.some((row) => row.id === item.id)) continue;
    out.push(item);
    if (out.length >= 3) break;
  }
  return out;
}

/** Views, die nach Reload wiederhergestellt werden dürfen (Premium-Gates). */
export function canRestorePortalView(
  view: PortalView,
  ctx: PortalNavContext,
): boolean {
  if (!portalViewAllowed(view, ctx.seat ?? {})) return false;
  if (view === 'document_editor' || view === 'vertrags_qa') {
    return ctx.isPremium;
  }
  if (view === 'contract_generator') {
    return ctx.isPremium && ctx.contractGeneratorEnabled;
  }
  if (view === 'kanzlei_zugaenge') {
    return Boolean(ctx.teamAccessEnabled);
  }
  if (view === 'gedaechtnis') {
    return ctx.isPremium && Boolean(ctx.memcoreEnabled);
  }
  if (view === 'matrix') {
    return kanzleiChatNavVisible(ctx.elementSeat);
  }
  return RESTORABLE_VIEWS.includes(view);
}
